import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../contexts/AuthContext';

export default function Account() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [contentPrefs, setContentPrefs] = useState('');
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [connections, setConnections] = useState({ youtube: null, kick: null, twitch: null });
  const [disconnecting, setDisconnecting] = useState({});
  const [threshold, setThreshold] = useState(null);
  const [thresholdEnabled, setThresholdEnabled] = useState(false);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  // Profile form
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  useEffect(() => {
    API.get('/api/billing/status')
      .then(r => { setBilling(r.data); setBillingLoading(false); })
      .catch(() => setBillingLoading(false));

    API.get('/api/settings/auto-post-threshold').then(r => {
      if (r.data.threshold) { setThresholdEnabled(true); setThreshold(r.data.threshold); }
      else { setThresholdEnabled(false); setThreshold(7); }
    }).catch(() => {});

    API.get('/api/settings/content-preferences').then(r => {
      setContentPrefs(r.data.preferences || '');
    }).catch(() => {});

    // Load connection status
    Promise.all([
      API.get('/api/auth/youtube/status').catch(() => ({ data: { connected: false } })),
      API.get('/api/streamers').catch(() => ({ data: [] })),
      API.get('/api/auth/twitch/status').catch(() => ({ data: { connected: false } })),
    ]).then(([yt, str, tw]) => {
      const streamer = str.data?.[0];
      const kickConnected = streamer?.kick_verified || (streamer?.slug && !streamer.slug.startsWith('user_'));
      setConnections({
        youtube: yt.data.connected,
        kick: kickConnected ? streamer.slug : null,
        twitch: tw.data.connected ? tw.data.username : null,
      });
    });
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await API.post('/api/billing/portal', { returnUrl: window.location.href });
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      setPortalLoading(false);
    }
  };

  const connectTwitch = async () => {
    try {
      const res = await API.get('/api/auth/twitch');
      window.location.href = res.data.url;
    } catch (err) { console.error(err); }
  };

  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect your ${platform} account? You'll need to reconnect it to use Clump with ${platform}.`)) return;
    setDisconnecting(d => ({...d, [platform]: true}));
    try {
      await API.post(`/api/auth/disconnect/${platform}`);
      setConnections(c => ({...c, [platform]: null}));
    } catch (err) {
      console.error(err);
    } finally {
      setDisconnecting(d => ({...d, [platform]: false}));
    }
  };

  const saveContentPrefs = async () => {
    setPrefsSaving(true);
    try {
      await API.post('/api/settings/content-preferences', { preferences: contentPrefs });
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setPrefsSaving(false); }
  };

  const saveThreshold = async (enabled, val) => {
    setThresholdSaving(true);
    try {
      await API.post('/api/settings/auto-post-threshold', { threshold: enabled ? val : null });
    } catch (err) { console.error(err); }
    finally { setThresholdSaving(false); }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const body = { display_name: displayName, email };
      if (newPassword) { body.current_password = currentPassword; body.new_password = newPassword; }
      await API.put('/api/auth/profile', body);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' });
      setCurrentPassword(''); setNewPassword('');
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setProfileSaving(false);
    }
  };

  const fmt = (date) => new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const statusLabel = () => {
    if (!billing || billing.status === 'none') return { text: 'No subscription', color: 'var(--text-muted)' };
    if (billing.status === 'trialing') return { text: `Free trial — ${billing.trialDaysRemaining} days left`, color: 'var(--accent)' };
    if (billing.status === 'active') return { text: 'Active — £19/month', color: 'var(--accent)' };
    if (billing.status === 'cancelled' || billing.status === 'canceled') return { text: 'Cancelled', color: '#ff4444' };
    return { text: billing.status, color: 'var(--text-muted)' };
  };

  const s = statusLabel();

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Account</div>
        <div className="page-subtitle">Manage your profile and subscription</div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20}}>

        {/* Subscription */}
        <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28}}>
          <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:20}}>Subscription</div>

          {billingLoading ? (
            <div style={{color:'var(--text-muted)', fontSize:13}}>Loading...</div>
          ) : (
            <>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                <div style={{fontSize:13, color:'var(--text-muted)'}}>Status</div>
                <div style={{fontSize:13, fontWeight:600, color: s.color}}>{s.text}</div>
              </div>

              {billing?.trialEnd && billing.status === 'trialing' && (
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                  <div style={{fontSize:13, color:'var(--text-muted)'}}>Trial ends</div>
                  <div style={{fontSize:13}}>{fmt(billing.trialEnd)}</div>
                </div>
              )}

              {billing?.periodEnd && billing.status === 'active' && (
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                  <div style={{fontSize:13, color:'var(--text-muted)'}}>Next payment</div>
                  <div style={{fontSize:13}}>{fmt(billing.periodEnd)}</div>
                </div>
              )}

              <div style={{borderTop:'1px solid var(--border)', paddingTop:16, marginTop:8, display:'flex', flexDirection:'column', gap:8}}>
                {billing?.isActive && (
                  <button className="btn btn-ghost" style={{width:'100%', fontSize:13}} onClick={openPortal} disabled={portalLoading}>
                    {portalLoading ? 'Loading...' : 'Manage subscription →'}
                  </button>
                )}
                {(!billing?.isActive) && (
                  <button className="btn btn-primary" style={{width:'100%', fontSize:13}} onClick={() => window.location.href='/billing'}>
                    Start free trial →
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Profile */}
        <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28}}>
          <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:20}}>Profile</div>

          <div style={{display:'flex', flexDirection:'column', gap:12}}>
            <div>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:6}}>Display name</div>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                style={{width:'100%', padding:'9px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none'}}
              />
            </div>
            <div>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:6}}>Email</div>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{width:'100%', padding:'9px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none'}}
              />
            </div>
            <div style={{borderTop:'1px solid var(--border)', paddingTop:12, marginTop:4}}>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:10}}>Change password</div>
              <input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                style={{width:'100%', padding:'9px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none', marginBottom:8}}
              />
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                style={{width:'100%', padding:'9px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none'}}
              />
            </div>

            {profileMsg && (
              <div style={{fontSize:12, color: profileMsg.type==='success' ? 'var(--accent)' : '#ff4444', padding:'8px 12px', background: profileMsg.type==='success' ? 'var(--accent-dim)' : 'rgba(255,68,68,0.08)', borderRadius:6}}>
                {profileMsg.text}
              </div>
            )}

            <button className="btn btn-primary" style={{width:'100%', marginTop:4}} onClick={saveProfile} disabled={profileSaving}>
              {profileSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

      </div>

      {/* Connected accounts */}
      <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28, marginTop:20}}>
        <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:20}}>Connected accounts</div>
        <div style={{display:'flex', flexDirection:'column', gap:12}}>
          {[
            { id:'youtube', label:'YouTube', icon:'📺', desc: connections.youtube ? 'Connected' : 'Not connected' },
            { id:'kick', label:'Kick', icon:'🟢', desc: connections.kick ? `@${connections.kick}` : 'Not connected' },
            { id:'twitch', label:'Twitch', icon:'🟣', desc: connections.twitch ? `@${connections.twitch}` : 'Not connected' },
          ].map(({ id, label, desc }) => (
            <div key={id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', background:'var(--surface2)', borderRadius:8}}>
              <div>
                <div style={{fontWeight:600, fontSize:14}}>{label}</div>
                <div style={{fontSize:12, color: connections[id] ? 'var(--accent)' : 'var(--text-muted)', marginTop:2}}>{desc}</div>
              </div>
              {connections[id] ? (
                <button
                  className="btn btn-ghost"
                  style={{fontSize:12, padding:'6px 14px', color:'#ff4444', borderColor:'rgba(255,68,68,0.3)', width:'auto'}}
                  onClick={() => disconnect(id)}
                  disabled={disconnecting[id]}
                >
                  {disconnecting[id] ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <div style={{fontSize:12, color:'var(--text-muted)'}}>
                  {id === 'youtube' ? <a href="/dashboard" style={{color:'var(--accent)', textDecoration:'none'}}>Connect →</a>
                   : id === 'kick' ? <a href="/dashboard" style={{color:'var(--accent)', textDecoration:'none'}}>Connect →</a>
                   : 'Coming soon'}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Auto-post threshold */}
      <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28, marginTop:20}}>
        <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:4}}>Auto-post threshold</div>
        <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:20}}>
          Clips scored at or above this threshold will be automatically approved and scheduled without review.
        </div>
        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
          <div
            onClick={() => { setThresholdEnabled(e => !e); saveThreshold(!thresholdEnabled, threshold || 7); }}
            style={{
              width:40, height:22, borderRadius:11, cursor:'pointer', transition:'all 0.2s',
              background: thresholdEnabled ? 'var(--accent)' : 'var(--border)',
              position:'relative', flexShrink:0,
            }}
          >
            <div style={{
              width:16, height:16, borderRadius:8, background:'#fff',
              position:'absolute', top:3, transition:'all 0.2s',
              left: thresholdEnabled ? 21 : 3,
            }}/>
          </div>
          <div style={{fontSize:13, fontWeight:500}}>
            {thresholdEnabled ? `Auto-approve clips scoring ${threshold}/10 or above` : 'Disabled — all clips need manual review'}
          </div>
        </div>
        {thresholdEnabled && (
          <div>
            <input
              type="range" min={5} max={10} step={1}
              value={threshold || 7}
              onChange={e => setThreshold(parseInt(e.target.value))}
              onMouseUp={e => saveThreshold(true, parseInt(e.target.value))}
              onTouchEnd={e => saveThreshold(true, parseInt(e.target.value))}
              style={{width:'100%', accentColor:'var(--accent)', marginBottom:8}}
            />
            <div style={{display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-muted)'}}>
              {[5,6,7,8,9,10].map(n => (
                <span key={n} style={{color: n === threshold ? 'var(--accent)' : undefined, fontWeight: n === threshold ? 700 : 400}}>{n}/10</span>
              ))}
            </div>
            {thresholdSaving && <div style={{fontSize:11, color:'var(--text-muted)', marginTop:6}}>Saving...</div>}
          </div>
        )}
      </div>

      {/* Questionnaire */}
      <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28, marginTop:20}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:4}}>Onboarding questionnaire</div>
            <div style={{fontSize:13, color:'var(--text-muted)'}}>Retake to update your content type, goals and audience info</div>
          </div>
          <button className="btn btn-ghost" style={{width:'auto', fontSize:13}} onClick={() => navigate('/onboarding')}>
            Retake quiz →
          </button>
        </div>
      </div>

      {/* Content preferences */}
      <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28, marginTop:20}}>
        <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:4}}>Content preferences</div>
        <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:16}}>
          Tell Clump what your audience loves — this helps the AI score and title clips better for your channel.
        </div>
        <textarea
          value={contentPrefs}
          onChange={e => setContentPrefs(e.target.value)}
          placeholder="e.g. My audience loves gym fails, funny reactions to chat, and motivational moments. They hate boring talking-head segments."
          rows={4}
          style={{width:'100%', padding:'10px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box'}}
        />
        <button
          className="btn btn-primary"
          style={{marginTop:12, width:'auto'}}
          onClick={saveContentPrefs}
          disabled={prefsSaving}
        >
          {prefsSaving ? 'Saving...' : 'Save preferences'}
        </button>
        {prefsSaved && <span style={{marginLeft:12, fontSize:12, color:'var(--accent)'}}>✓ Saved</span>}
      </div>
    </div>
  );
}
